import { useEffect, useState, useCallback } from "react";
import { PortfolioAllocationsGrid } from "@/components/portfolio/build/PortfolioAllocationsGrid";
import { PortfolioSummary } from "@/components/portfolio/build/PortfolioSummary";
import { PortfolioActions } from "@/components/portfolio/build/PortfolioActions";
import { useAssetClassAllocations } from "../hooks/useAssetClassAllocations";
import { useAllocationManagement } from "../hooks/useAllocationManagement";
import { usePortfolioSizeLock } from "../../../../hooks/usePortfolioSizeLock";
import { useBulkSelection } from "../../../../hooks/useBulkSelection";
import { type CalculationMode } from "@/lib/portfolioCalculations";
import { useDebouncedPortfolioSize } from "@/hooks/useDebouncedPortfolioSize";
import { ASSET_CLASS_LABELS, standardAssetClasses } from "@/utils/asset-class";
import { useIsMobile } from "@/hooks/use-mobile";
import { PortfolioBulkSelectionBar } from "@/components/portfolio/build/PortfolioBulkSelectionBar";
import { PortfolioHeaderBar } from "@/components/portfolio/build/PortfolioHeaderBar";
import { PortfolioSizeControls } from "@/components/portfolio/build/PortfolioSizeControls";
import { usePortfolioTotals } from "@/components/portfolio/build/hooks/usePortfolioTotals";
import { usePortfolioAutoSize } from "@/components/portfolio/build/hooks/usePortfolioAutoSize";
import { useEffectiveAllocations } from "@/components/portfolio/build/hooks/useEffectiveAllocations";
import { useOverrideReconciliation } from "@/components/portfolio/build/hooks/useOverrideReconciliation";
import { usePendingOversizeFlush } from "@/components/portfolio/build/hooks/usePendingOversizeFlush";

interface PortfolioContentProps {
  portfolio: any;
  portfolioId: string;
  portfolioName: string;
  portfolioSize: number;
  isEditingName: boolean;
  setIsEditingName: (value: boolean) => void;
  onPortfolioNameChange: (name: string) => void;
  onPortfolioSizeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPortfolioSizeUpdate?: (newSize: number) => Promise<boolean>;
  onNameSave: () => void;
  onAssetAdded: () => void;
  onDeleteClick: () => void;
  onSaveClick: () => Promise<void>;
  isDeleteDialogOpen: boolean;
  setIsDeleteDialogOpen: (open: boolean) => void;
  portfolioType?: string;
}

export const PortfolioContent = ({
  portfolio,
  portfolioId,
  portfolioName,
  portfolioSize,
  isEditingName,
  setIsEditingName,
  onPortfolioNameChange,
  onNameSave,
  onAssetAdded,
  onDeleteClick,
  onSaveClick,
  isDeleteDialogOpen,
  setIsDeleteDialogOpen,
  portfolioType,
}: PortfolioContentProps) => {
  const isMobile = useIsMobile();
  const assetClasses = standardAssetClasses
    .filter(id => id !== 'other' && id !== 'unclassified') // Do not render a separate Unclassified section
    .map(id => ({
      id,
      title: ASSET_CLASS_LABELS[id] || id
    }));

  const { allocatedAssets, getAllocationsForAssetClass } = useAssetClassAllocations(portfolio?.portfolio_allocations);
  
  // Get portfolio size lock state
  const { isPortfolioSizeLocked } = usePortfolioSizeLock(portfolioId);

  // Determine portfolio type and mode (stable for the lifetime of this view)
  const type = portfolioType ?? portfolio?.portfolio_type;
  const calcMode: CalculationMode = type === 'model' ? 'percentage' : 'dollar';

  // Optimistic overrides: allocationId -> percentage
  const [overrides, setOverrides] = useState<Record<string, number>>({});
  // Track when a portfolio size update completes so we can flush pending oversize edits
  const [sizeSavedAt, setSizeSavedAt] = useState<number>(0);
  // Portfolio currency (per-portfolio, defaulted by user profile). Stored via PortfolioCurrencySelect in localStorage
  const [portfolioCurrency, setPortfolioCurrency] = useState<string>('CAD');

  // Use debounced portfolio size management
  const debouncedSize = useDebouncedPortfolioSize({
    portfolioId,
    initialSize: portfolioSize,
    onSuccess: () => {
      // Mark a successful size commit; a listener will persist any pending oversize edits.
      setSizeSavedAt(Date.now());
    },
    portfolioType: type,
    currencyCode: portfolioCurrency,
  });

  // Reconcile overrides with fresh DB values without clearing prematurely
  useOverrideReconciliation(portfolio?.portfolio_allocations, setOverrides);

  const baseAllocations = portfolio?.portfolio_allocations || [];
  const effectiveAllocations = useEffectiveAllocations(
    baseAllocations,
    overrides,
    calcMode,
    debouncedSize.portfolioSize
  );

  const { computedTotals, persistedTotalsDollar, fallbackTotals, displayTotals, hasOverflowForUi, overrideSum } = usePortfolioTotals(
    effectiveAllocations,
    baseAllocations,
    debouncedSize.portfolioSize,
    calcMode,
    overrides
  );

  // Debug logging to track state synchronization (DEV only)
  if (import.meta.env.DEV) {
    console.debug('PortfolioContent Debug:', {
      portfolioSize: debouncedSize.portfolioSize,
      inputPortfolioSize: portfolioSize,
      overrides,
      baseAllocations: baseAllocations.length,
      effectiveAllocations: effectiveAllocations.length,
      overrideSum,
      computedTotals,
      displayTotals: displayTotals,
      hasOverflow: fallbackTotals.hasOverflow,
      isPortfolioSizeLocked
    });
  }

  // Handle auto-size: expand portfolio to fit total allocations
  const handleAutoSize = usePortfolioAutoSize(
    calcMode,
    debouncedSize.portfolioSize,
    debouncedSize.updatePortfolioSize,
    setOverrides,
    computedTotals,
    persistedTotalsDollar,
    fallbackTotals,
  );

  // Allocation management (needed by pending oversize flush)
  const { handleBulkDelete, handleUpdateAllocationByValue } = useAllocationManagement(onAssetAdded, portfolioCurrency, portfolioId);

  // After a portfolio size save completes, persist any pending oversize allocation now that it won't violate constraints
  usePendingOversizeFlush(
    sizeSavedAt,
    debouncedSize.portfolioSize,
    isPortfolioSizeLocked,
    setOverrides,
    handleUpdateAllocationByValue,
    debouncedSize.updatePortfolioSize
  );

  // Get flat list of all allocations for bulk selection
  const allAllocations = portfolio?.portfolio_allocations || [];
  const [selectionState, selectionActions] = useBulkSelection(allAllocations.length);
  const { selectedIds, selectedCount } = selectionState;
  const { toggleItem, toggleAll, clearSelection } = selectionActions;

  const handleBulkDeleteClick = async () => {
    const selectedIdsArray = Array.from(selectedIds) as string[];
    await handleBulkDelete(selectedIdsArray);
    clearSelection();
  };

  // type already computed above

  // header display mapping handled inside PortfolioHeaderBar

  return (
    <div className="space-y-6">
      <PortfolioHeaderBar
        portfolio={portfolio}
        isEditingName={isEditingName}
        portfolioName={portfolioName}
        onNameChange={onPortfolioNameChange}
        onSave={onNameSave}
        onEditStart={() => setIsEditingName(true)}
        onEditCancel={() => {
          setIsEditingName(false);
          onPortfolioNameChange(portfolio?.name || "");
        }}
        type={type}
      />

      <PortfolioSizeControls
        portfolioId={portfolioId}
        type={type}
        isMobile={isMobile}
        portfolioSize={debouncedSize.portfolioSize}
        onPortfolioSizeChange={debouncedSize.handlePortfolioSizeChange}
        totalAllocations={displayTotals.totalDollarAmount}
        hasOverflow={hasOverflowForUi}
        onAutoSize={handleAutoSize}
        isLocked={isPortfolioSizeLocked}
        currency={portfolioCurrency}
        onCurrencyChange={setPortfolioCurrency}
        isSaving={debouncedSize.isSaving}
      />

      {selectedCount > 0 && (
        <PortfolioBulkSelectionBar
          selectedCount={selectedCount}
          isMobile={isMobile}
          onClearSelection={clearSelection}
          onBulkDeleteClick={handleBulkDeleteClick}
        />
      )}

        <PortfolioAllocationsGrid 
          assetClasses={assetClasses}
          getAllocationsForAssetClass={getAllocationsForAssetClass}
          portfolioId={portfolioId}
          onAssetAdded={onAssetAdded}
          portfolioSize={debouncedSize.portfolioSize}
          isPortfolioSizeLocked={isPortfolioSizeLocked}
          onPortfolioSizeChange={debouncedSize.updatePortfolioSize}
          selectedIds={selectedIds}
          toggleItem={toggleItem}
          toggleAll={toggleAll}
          mode={calcMode === 'percentage' ? 'percentage' : 'dollar'}
          overrides={overrides}
          onLocalPercentageChange={(id, pct) => setOverrides(prev => ({ ...prev, [id]: pct }))}
          onLocalDollarChange={(id, dollars) => {
            const size = debouncedSize.portfolioSize || 0;
            if (size > 0) {
              const pct = (dollars / size) * 100;
              setOverrides(prev => ({ ...prev, [id]: pct }));
            }
          }}
          currencyCode={portfolioCurrency}
        />

      <PortfolioSummary 
        portfolioSize={debouncedSize.portfolioSize}
        allocations={baseAllocations}
        onAutoSize={handleAutoSize}
        isPortfolioSizeLocked={isPortfolioSizeLocked}
        mode={calcMode}
        overrides={overrides}
        currency={portfolioCurrency}
      />

      <PortfolioActions
        portfolio={portfolio}
        onDeleteClick={onDeleteClick}
        onSaveClick={async () => {
          // Ensure any pending size change is committed before save
          await debouncedSize.updatePortfolioSize(debouncedSize.portfolioSize);
          await onSaveClick();
        }}
        isDeleteDialogOpen={isDeleteDialogOpen}
        setIsDeleteDialogOpen={setIsDeleteDialogOpen}
      />
    </div>
  );
};
